import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, concat, forkJoin, merge, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, flatMap, map, mergeMap, share, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LogCategory, LogCoreService, LoggerService, LogType } from '../../core/services/logger.service';
import { environment } from './../../../environments/environment';
import { ProfileService } from './profile.service';
import { UtilityService } from './utility.service';

@Injectable()
export class CmsService {

    private _reload$ = new Subject<void>();   
    private _eventAgenda$: Observable<any> = null;
    private _subProfile: Subscription = null;

    constructor(
        private http: HttpClient,
        private profile: ProfileService,
        private logger: LoggerService,
        private utility: UtilityService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);
        //subscrible to profile changes, so we can update this data when you spoof
        this._subProfile = profile.profile$.subscribe((data) => {
            this.update();
            this.log('constructor', 'profile.profile$ received', LogCategory.SubscriptionData);
        })
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreService, LogCoreService.Cms, category);
    }

    public contentTypes: any = {};  //  A collection of all the types
    public currentContentType$: BehaviorSubject<string> = new BehaviorSubject(""); // Use this for finding all of one type
    public get getContentByType$(): Observable<any> {
        if (!this.contentTypes[this.currentContentType$.value])
            this.contentTypes[this.currentContentType$.value] = this.http.get<any>(`${environment.apiUrl}/cms/content/${this.currentContentType$.value}/`)
            .pipe(
                tap((data) => this.log('contact$', 'Fetched contact list', LogCategory.SubscriptionData)),
                takeUntil(this._reload$),
                shareReplay(1)
            );

        return this.contentTypes[this.currentContentType$.value];
    }

    public currentSingleContent$: BehaviorSubject<any> = new BehaviorSubject(null); // Use this for finding a specific file of one type
    public getContentFile$: Observable<any> = this.currentSingleContent$
        .pipe(
            switchMap((item) => this.http.get<any>(`${environment.apiUrl}/cms/v2/content/${item.type}/${item.id}`)
                .pipe(
                    map((res) => {
                        let retVal = { "Title": "", "Html": "" };
                        if (res != null && res.Result)
                            retVal = res.Result;
                        return retVal;                        
                    }),
                    catchError((err) => {
                        return of(err);
                    })
                )
            ));


    public v2ContentTypes: any = {};  //  A collection of all the types
    public getV2ContentByType$(type: string): Observable<any[]> {
        if (!this.v2ContentTypes[type]) {
            this.v2ContentTypes[type] = this.http.get<any[]>(`${environment.apiUrl}/cms/v2/content/${type}`)
                .pipe(
                    tap((data) => this.log('getV2ContentByType$', 'Fetched cms data', LogCategory.SubscriptionData)),
                    takeUntil(this._reload$),
                    shareReplay(1)
                );
        }

        return this.v2ContentTypes[type];
    }

    public contentTypesChanged$: Subject<string[]> = new Subject<string[]>();
    public getV2ContentByTypes$: Observable<any[]> = this.contentTypesChanged$.pipe(
        switchMap((types) =>
            forkJoin([...types.map(t => this.http.get<any[]>(`${environment.apiUrl}/cms/v2/content/${t}`))])
        ),
        map((result) => result.flat().sort((a, b) => a.Title > b.Title ? 1 : -1)),
    );

    public v2Content: any;
    public specificContentItem$: any;
    public getV2ContentByTypeAndID$(type: string, id: string): Observable<any> {
        this.specificContentItem$ = this.http.get<any>(`${environment.apiUrl}/cms/v2/content/${type}/${id}`)
            .pipe(
                tap((data) => this.log('getV2ContentByTypeAndID$', 'Fetched tiles cms data', LogCategory.SubscriptionData)),
                takeUntil(this._reload$),
                shareReplay(1)
            );

        return this.specificContentItem$;
    }

    //  Behavior subject used to indicate if the file access was successful on the document page (and potentially other places).
    public downloadFileFound$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public downloadById(id: string, event: any) {
        //  If we are coming to this function from a button click, prevent any default actions
        if (!!event) {
            event.preventDefault();
            event.stopPropagation();
        }

        //  These files always seem to be the "Documents" type.  We can make this more generic if needed.
        this.http.get(`${environment.apiUrl}/cms/v2/content/download/${id}/Documents`, { responseType: 'blob', observe: 'response' })
            .pipe(
                share(),
                tap((res: HttpResponse<Blob>) => {
                    var file = {
                        filename: res.headers.get('Content-Disposition').split('filename=')[1].split(';')[0],
                        body: new Blob([res.body], { type: res.headers.get('content-type') })
                    };
                    var url = window.URL.createObjectURL(file.body);
                    //  Blobs do not have file names.  This allows for an automatic download of the file while
                    //  retaining the original file name.
                    this.utility.downloadFile(`${file.filename}`, url);
                    this.downloadFileFound$.next(true);
                    return res;

                }),
                catchError((error) => {
                    this.downloadFileFound$.next(false);
                    if (error.status === 404)
                        return of(undefined);
                    else
                        throw error;
                }),
            ).subscribe(); //  Finite observable, no need to unsubscribe.
    }

    public update() {
        this.log('update', 'Cms updated');
        this._reload$.next();        
        this.contentTypes = {};
    }
}
